/**
 * useCalibrationActions Hook
 * 
 * Orchestrates calibration actions using builder hooks and helper utilities.
 * Provides action handlers for calibration mode interactions.
 */

import { useCallback } from 'react';
import type { ICalibrationAvailability } from '../types/calibration';
import { useCalibrationFilterBuilder } from './useCalibrationFilterBuilder';
import {
  updateVisualizationMetrics,
  buildCalibrationUrlParams,
  syncCalibrationViewState,
  getUSOnlyRegionFilter,
} from '../utils/calibrationHelpers';
import { logCalibrationClick } from '../utils/loggingUtils';
import { FILTER_URL_CONFIG } from '@intelligence/_config/components/filterUrl/kpiTrends';
import { reportButtonLog } from '@intelligence/v3/utils/logReporter';
import { dbiBtnLogMes } from '@/pages/intelligence/utils/buriedPointCfg';

interface UseCalibrationActionsParams {
  calibrationFlags: ICalibrationAvailability | null;
  filterValue: any;
  releaseTime: string;
  defaultInputList: any[];
  byDevice: boolean;
  gameId: string;
  gameName: string;
  setFilterValue: (v: any) => void;
  updateUrl?: (params: any, config: any, flag: boolean) => void;
  updateFilterDatas: (...args: any[]) => void;
  setKpiVisualMetricValue: (v: string) => void;
  setVisualMetricValue: (v: string) => void;
  setVisualMetricValues: (v: string[]) => void;
  jumpVisualMetricsRef: React.MutableRefObject<string[] | null>;
  setIsDataBrainTrueDataMode: (v: string) => void;
  setIsCalibrationMetricsSwitcherDisabled: (v: boolean) => void;
  // Refs
  userManuallyChangedRegionRef: React.MutableRefObject<boolean>;
  userSegmentChangedRegionRef: React.MutableRefObject<boolean>;
  userSegmentCloseRef: React.MutableRefObject<boolean>;
  restoreDefault: React.MutableRefObject<boolean>;
  specialCascader: React.MutableRefObject<boolean>;
}

interface UseCalibrationActionsResult {
  handleViewCalibrationData: (metric: string) => void;
  switchToCalibrationMode: () => void;
  handleSetRegionUSOnly: () => void;
}

/**
 * Hook to provide calibration action handlers
 * 
 * Provides three main actions:
 * 1. handleViewCalibrationData - Handles "View calibration raw data" clicks
 * 2. switchToCalibrationMode - Handles "Switch to Calibration Metrics" button
 * 3. handleSetRegionUSOnly - Handles M Science region adjustment
 * 
 * @param params - Configuration and dependencies
 * @returns Action handler functions
 * 
 * @example
 * ```typescript
 * const { handleViewCalibrationData, switchToCalibrationMode } = useCalibrationActions({
 *   calibrationFlags,
 *   filterValue,
 *   // ... other params
 * });
 * ```
 */
export function useCalibrationActions(
  params: UseCalibrationActionsParams
): UseCalibrationActionsResult {
  const {
    calibrationFlags,
    filterValue,
    releaseTime,
    defaultInputList,
    byDevice,
    gameId,
    gameName,
    setFilterValue,
    updateUrl,
    updateFilterDatas,
    setKpiVisualMetricValue,
    setVisualMetricValue,
    setVisualMetricValues,
    jumpVisualMetricsRef,
    setIsDataBrainTrueDataMode,
    setIsCalibrationMetricsSwitcherDisabled,
    userManuallyChangedRegionRef,
    userSegmentChangedRegionRef,
    userSegmentCloseRef,
    restoreDefault,
    specialCascader,
  } = params;

  const { buildCalibrationFilters, buildSwitcherFilters } = useCalibrationFilterBuilder({
    calibrationFlags,
    releaseTime,
  });

  /**
   * Handle "View calibration raw data" click from metric explanation tooltip
   */
  const handleViewCalibrationData = useCallback(
    (metric: string) => {
      console.log('[handleViewCalibrationData] Starting calibration view for metric:', metric);

      // Log the action
      logCalibrationClick(metric, gameId, gameName);

      // Build filter parameters
      const filterParams = buildCalibrationFilters(metric, filterValue);

      // Update visualization metrics
      updateVisualizationMetrics(
        metric,
        calibrationFlags,
        { setKpiVisualMetricValue, setVisualMetricValue, setVisualMetricValues },
        jumpVisualMetricsRef
      );

      // Build URL parameters
      const visualMetric = jumpVisualMetricsRef.current?.[0] || metric;
      const urlParams = buildCalibrationUrlParams(metric, filterParams, visualMetric, byDevice);

      // Sync state and trigger data fetch
      syncCalibrationViewState(
        filterParams,
        urlParams,
        { setFilterValue, updateUrl, updateFilterDatas },
        {
          userManuallyChangedRegionRef,
          userSegmentChangedRegionRef,
          userSegmentCloseRef,
          restoreDefault,
          specialCascader,
        },
        FILTER_URL_CONFIG
      );

      console.log('[handleViewCalibrationData] Calibration view updated successfully');
    },
    [
      calibrationFlags,
      filterValue,
      byDevice,
      gameId,
      gameName,
      buildCalibrationFilters,
      setFilterValue,
      updateUrl,
      updateFilterDatas,
      setKpiVisualMetricValue,
      setVisualMetricValue,
      setVisualMetricValues,
      jumpVisualMetricsRef,
      userManuallyChangedRegionRef,
      userSegmentChangedRegionRef,
      userSegmentCloseRef,
      restoreDefault,
      specialCascader,
    ]
  );

  /**
   * Handle "Switch to Calibration Metrics" button click
   */
  const switchToCalibrationMode = useCallback(() => {
    console.log('[switchToCalibrationMode] Switching to calibration metrics mode');

    try {
      // Build filter parameters
      const filterParams = buildSwitcherFilters(filterValue, defaultInputList);
      const visualMetric = 'databrain__total_revenue';

      // Update mode state
      setIsDataBrainTrueDataMode('true');
      setIsCalibrationMetricsSwitcherDisabled(true);

      // Update visualization metrics
      setKpiVisualMetricValue(visualMetric);
      setVisualMetricValue(visualMetric);
      setVisualMetricValues([visualMetric]);
      jumpVisualMetricsRef.current = [visualMetric];

      // Build URL parameters
      const urlParams = {
        ...filterParams,
        visualMetricValue: visualMetric,
        byDevice,
      };

      // Sync state and trigger data fetch
      syncCalibrationViewState(
        filterParams,
        urlParams,
        { setFilterValue, updateUrl, updateFilterDatas },
        {
          userManuallyChangedRegionRef,
          userSegmentChangedRegionRef,
          userSegmentCloseRef,
          restoreDefault,
          specialCascader,
        },
        FILTER_URL_CONFIG
      );

      // Log the action
      const btnLogCfg = dbiBtnLogMes.calibrationMetricsSwitcherClick;
      reportButtonLog({
        ...btnLogCfg,
        extInfo: `${gameId}: switch to databrain calibration metrics`,
      });

      console.log('[switchToCalibrationMode] Successfully switched to calibration mode');
    } catch (error) {
      console.error('[switchToCalibrationMode] Failed to switch to calibration mode:', error);
    }
  }, [
    filterValue,
    defaultInputList,
    byDevice,
    gameId,
    buildSwitcherFilters,
    setFilterValue,
    updateUrl,
    updateFilterDatas,
    setKpiVisualMetricValue,
    setVisualMetricValue,
    setVisualMetricValues,
    jumpVisualMetricsRef,
    setIsDataBrainTrueDataMode,
    setIsCalibrationMetricsSwitcherDisabled,
    userManuallyChangedRegionRef,
    userSegmentChangedRegionRef,
    userSegmentCloseRef,
    restoreDefault,
    specialCascader,
  ]);

  /**
   * Handle "Set Region to US Only" for M Science data
   */
  const handleSetRegionUSOnly = useCallback(() => {
    console.log('[handleSetRegionUSOnly] Setting region to US only');

    const filterValueTmp = getUSOnlyRegionFilter(filterValue);

    // Update state and trigger data fetch
    syncCalibrationViewState(
      filterValueTmp,
      { ...filterValueTmp, byDevice },
      { setFilterValue, updateUrl, updateFilterDatas },
      {
        userManuallyChangedRegionRef,
        userSegmentChangedRegionRef,
        userSegmentCloseRef,
        restoreDefault,
        specialCascader,
      },
      FILTER_URL_CONFIG
    );
  }, [
    filterValue,
    byDevice,
    setFilterValue,
    updateUrl,
    updateFilterDatas,
    userManuallyChangedRegionRef,
    userSegmentChangedRegionRef,
    userSegmentCloseRef,
    restoreDefault,
    specialCascader,
  ]);

  return {
    handleViewCalibrationData,
    switchToCalibrationMode,
    handleSetRegionUSOnly,
  };
}
